// Browser side of the push reminders.
//
// The server is reached through three endpoints under /api. When they are not
// deployed — a preview without the environment set, or a plain static host —
// every call here fails softly and the UI says reminders are unavailable
// rather than breaking.

const SW_PATH = '/sw.js';

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// Standalone display means the app was added to the home screen, which is what
// iOS requires before it will deliver web push at all.
export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const permission = () => (('Notification' in window) ? Notification.permission : 'unsupported');

export const timeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'UTC';
  }
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
};

export const fetchPushConfig = async () => {
  try {
    const response = await fetch('/api/push-config');
    if (!response.ok) return { configured: false, publicKey: '' };
    return await response.json();
  } catch (error) {
    return { configured: false, publicKey: '' };
  }
};

export const getRegistration = async () => {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH);
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return null;
  }
};

export const getSubscription = async () => {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!registration) return null;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    return null;
  }
};

const postJson = async (path, body) => {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail.error || `Request failed (${response.status})`);
  }
  return response.json();
};

/**
 * Ask for permission if needed, subscribe, and register the chosen hours.
 * Returns { ok } or { ok: false, reason } — the reason is meant to be shown.
 */
export const enablePush = async ({ hours, goalMetOn }) => {
  if (!isPushSupported()) {
    return { ok: false, reason: 'This browser does not support notifications.' };
  }
  if (isIos() && !isStandalone()) {
    return {
      ok: false,
      reason: 'On iPhone and iPad, add this app to your home screen first — Safari only delivers notifications to installed apps.'
    };
  }

  const config = await fetchPushConfig();
  if (!config.configured || !config.publicKey) {
    return { ok: false, reason: 'Notifications are not configured on this deployment yet.' };
  }

  const granted = await Notification.requestPermission();
  if (granted !== 'granted') {
    return { ok: false, reason: 'Notifications are blocked. Allow them for this site in your browser settings.' };
  }

  const registration = await getRegistration();
  if (!registration) return { ok: false, reason: 'The background worker could not start.' };

  try {
    const existing = await registration.pushManager.getSubscription();
    const subscription = existing || await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey)
    });

    await postJson('/api/subscribe', {
      subscription: subscription.toJSON(),
      timeZone: timeZone(),
      hours,
      enabled: true,
      goalMetOn: goalMetOn || null
    });

    return { ok: true };
  } catch (error) {
    console.error('Could not enable push:', error);
    return { ok: false, reason: 'Could not register for notifications. Please try again.' };
  }
};

// Update hours or the "already read today" marker for an existing subscription.
// Silent by design: it runs in the background and must never interrupt reading.
export const syncPush = async ({ hours, enabled, goalMetOn }) => {
  const subscription = await getSubscription();
  if (!subscription) return false;
  try {
    await postJson('/api/subscribe', {
      subscription: subscription.toJSON(),
      timeZone: timeZone(),
      hours,
      enabled,
      goalMetOn: goalMetOn || null
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const disablePush = async () => {
  const subscription = await getSubscription();
  if (!subscription) return true;
  const { endpoint } = subscription.toJSON();
  try {
    await subscription.unsubscribe();
  } catch (error) {
    // Even if the browser refuses, stop the server from sending
  }
  try {
    await postJson('/api/unsubscribe', { endpoint });
  } catch (error) {
    return false;
  }
  return true;
};

export const MAX_REMINDER_HOURS = 4;
